import { LoadingOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import {
  useBeforeUnload,
  unstable_useBlocker as useBlocker,
  unstable_Blocker as Blocker,
  unstable_BlockerFunction as BlockerFunction,
} from "react-router-dom";

const usePrompt = (when: boolean) => {
  const intl = useIntl();
  const blockerRef = useRef<Blocker>();
  const confirmRef = useRef<ReturnType<typeof Modal.confirm>>();

  const blockerFn = useCallback<BlockerFunction>(
    ({ nextLocation }) => {
      const { skipBlock } = nextLocation?.state || {};

      if (skipBlock) {
        confirmRef.current?.destroy();

        return false;
      }

      return when;
    },
    [when]
  );

  const onBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (when) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = intl.formatMessage({
          defaultMessage: "Data saving in progress. Please wait.",
        });
      }
    },
    [intl, when]
  );

  const reset = useCallback(() => {
    blockerRef.current?.reset?.();
    confirmRef.current?.destroy();
  }, []);

  const blocker = useBlocker(blockerFn);

  useEffect(() => {
    blockerRef.current = blocker;
  }, [blocker]);

  useEffect(() => {
    if (blocker.state === "blocked" && !when) {
      confirmRef.current?.destroy();
      blocker.reset();
    }
  }, [blocker, intl, when]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      confirmRef.current = Modal.confirm({
        title: intl.formatMessage({
          defaultMessage: "Saving Data",
        }),
        content: intl.formatMessage({
          defaultMessage: "Data saving in progress. Please wait.",
        }),
        icon: <LoadingOutlined spin />,
        cancelButtonProps: {
          style: {
            display: "none",
          },
        },
      });
    }
  }, [blocker.state, intl]);

  useBeforeUnload(onBeforeUnload, { capture: true });

  return { blocker, reset };
};

export default usePrompt;
